import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import type { BcraCentralDeudoresConsulta } from '../../types/bcraCentralDeudores';
import { etiquetaNivelRiesgo } from '../../utils/bcraCentralDeudores';

interface BcraCentralDeudoresDialogProps {
  open: boolean;
  onClose: () => void;
  data: BcraCentralDeudoresConsulta | null;
}

const chipColor = (nivel: BcraCentralDeudoresConsulta['nivelRiesgo']) => {
  switch (nivel) {
    case 'riesgo':
      return 'error';
    case 'atencion':
      return 'warning';
    case 'normal':
      return 'success';
    default:
      return 'info';
  }
};

const formatMoney = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '-';
  return value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const BcraCentralDeudoresDialog: React.FC<BcraCentralDeudoresDialogProps> = ({
  open,
  onClose,
  data,
}) => {
  if (!data) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Central de Deudores — BCRA
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
          <Chip
            label={etiquetaNivelRiesgo(data.nivelRiesgo)}
            color={chipColor(data.nivelRiesgo)}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            CUIT {data.cuit}
            {data.denominacion ? ` — ${data.denominacion}` : ''}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
          {data.mensajeResumen}
        </Typography>

        {data.consultaParcial && data.errorConsulta && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Consulta parcial: {data.errorConsulta}
          </Alert>
        )}

        {data.periodo && (
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Período {data.periodo}
            {data.situacionMaxima != null ? ` — Situación máxima: ${data.situacionMaxima}` : ''}
          </Typography>
        )}

        {data.entidades.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Entidad</TableCell>
                  <TableCell align="right">Situación</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell align="right">Días atraso</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.entidades.map((e, idx) => (
                  <TableRow key={`${e.entidad}-${idx}`}>
                    <TableCell>{e.entidad || '-'}</TableCell>
                    <TableCell align="right">{e.situacion ?? '-'}</TableCell>
                    <TableCell align="right">{formatMoney(e.monto)}</TableCell>
                    <TableCell align="right">{e.diasAtrasoPago ?? '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {data.chequesRechazados.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Cheques rechazados
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nº cheque</TableCell>
                    <TableCell>Fecha rechazo</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>Causal</TableCell>
                    <TableCell>Fecha pago</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.chequesRechazados.map((c, idx) => (
                    <TableRow key={`${c.nroCheque}-${idx}`}>
                      <TableCell>{c.nroCheque}</TableCell>
                      <TableCell>{c.fechaRechazo || '-'}</TableCell>
                      <TableCell align="right">{formatMoney(c.monto)}</TableCell>
                      <TableCell>{c.causal || '-'}</TableCell>
                      <TableCell>{c.fechaPago || 'Impago'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Información pública del BCRA (Central de Deudores). No constituye un scoring propio de la empresa.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BcraCentralDeudoresDialog;
